import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { campusApi, tagApi } from "service/api";
import { logger } from "utils";

/**
 * panelApi (RTK Query)
 * --------------------
 * Data many screens share: the signed-in user (userEndpoints.js), the
 * tag, tag icon and campus lists of a district, its dashboard numbers
 * (dashboardEndpoints.js), and one campus / domain (detailEndpoints.js).
 * Requests still go through the service layer (`service/api`), so the axios
 * instance with its token refresh and the mappers are reused.
 *
 * - One request per list and argument, however many components use it
 * - After a change, invalidate the list's tag and every screen refetches:
 *     dispatch(invalidateLists(LIST.tags))
 * - Sign out drops the whole cache (ProtectedRoute: resetApiState)
 */
export const LIST = {
  currentUser: "CurrentUser",
  tags: "Tag",
  tagIcons: "TagIcon",
  campuses: "Campus",
  dashboard: "Dashboard",
  campus: "CampusDetail",
  domain: "DomainDetail",
};

// Error shape the old slices stored: API body + HTTP status, or the message
// of a network error
export const toQueryError = (error) =>
  error?.response
    ? { ...error.response.data, status: error.response.status }
    : { status: undefined, message: error?.message };

// Runs a service call and returns RTK Query's { data } / { error } result
export const runQuery = async (request) => {
  try {
    return { data: await request() };
  } catch (error) {
    return { error: toQueryError(error) };
  }
};

const MAX_PAGES = 200; // safety stop if the API keeps returning full pages

/**
 * Loads every page of a paged list: page 1, 2, … until a page has fewer
 * rows than `limit`. `fetchPage({ limit, page })` returns the page's rows.
 */
export const fetchAllPages = async (fetchPage, limit) => {
  const all = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const rows = (await fetchPage({ limit, page })) ?? [];
    all.push(...rows);
    if (rows.length < limit) break;
  }
  return all;
};

export const panelApi = createApi({
  reducerPath: "panelApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: Object.values(LIST),
  endpoints: (build) => ({
    // Tags of a district (all pages)
    getTags: build.query({
      queryFn: (districtId) =>
        runQuery(() =>
          fetchAllPages(
            async ({ limit, page }) =>
              (await tagApi.getList({ params: { district_id: districtId, limit, page } })).rowData,
            100,
          ),
        ),
      providesTags: [LIST.tags],
    }),

    // Tag icons: on an error the list stays empty and is not requested again
    getTagIcons: build.query({
      queryFn: async () => {
        try {
          return { data: (await tagApi.getIconList()).data };
        } catch (error) {
          logger.warn("Tag icons could not be loaded:", error);
          return { data: [] };
        }
      },
      providesTags: [LIST.tagIcons],
    }),

    // Campuses of a district (all pages)
    getCampuses: build.query({
      queryFn: (districtId) =>
        runQuery(() =>
          fetchAllPages(
            async ({ limit, page }) =>
              (await campusApi.getList({ params: { district_id: districtId, limit, page } })).rowData,
            25,
          ),
        ),
      providesTags: [LIST.campuses],
    }),
  }),
});

export const { useGetTagsQuery, useGetTagIconsQuery, useGetCampusesQuery } = panelApi;

// Action to mark lists as changed, e.g. invalidateLists(LIST.tags)
export const invalidateLists = (...types) => panelApi.util.invalidateTags(types);
